import type { Tinte } from '@/components/Editorial'
import { cn } from '@/lib/cn'

/**
 * Encabezado de las pantallas interiores.
 *
 * Es un **bloque de color a sangre**, igual que el de tinta que abre el inicio,
 * pero en el tinte de su sección. Esa diferencia es deliberada y carga la mitad
 * de la navegación de la app:
 *
 * - **Tinta** = la pantalla del dinero. Una sola, el inicio, y por eso el bloque
 *   oscuro conserva todo su peso: sostiene la cifra principal.
 * - **Tinte pastel** = una sección a la que se va. Cada una conserva su color en
 *   toda la app —lavanda las tarjetas, menta el ahorro, durazno lo que sale— así
 *   que se reconoce de un vistazo antes de leer el título.
 *
 * Antes era un encabezado claro con el texto a secas, indistinguible entre
 * pantallas: once secciones con el mismo aspecto y el nombre como única pista.
 *
 * Sigue siendo adherente y con velo: el título tiene que seguir ahí al bajar por
 * una lista larga. No lleva esquinas redondeadas por eso mismo —un bloque
 * redondeado del que sale contenido por debajo se lee como una tarjeta rota.
 *
 * El tema claro/oscuro se elige en Ajustes → Apariencia. Aquí había un botón
 * que lo alternaba: ocupaba una esquina en todas las pantallas para una
 * decisión que se toma una vez, y además no sabía expresar «automático», que es
 * la opción que la mayoría quiere.
 */

const FONDO: Record<Tinte, string> = {
  butter: 'bg-tint-butter',
  lavender: 'bg-tint-lavender',
  mint: 'bg-tint-mint',
  peach: 'bg-tint-peach',
  neutro: 'bg-bg/85',
}

export function ScreenHeader({
  title,
  subtitle,
  action,
  tinte = 'neutro',
}: {
  title: string
  subtitle?: string
  action?: React.ReactNode
  /** El color de la sección. Ver la nota de arriba: no es decorativo. */
  tinte?: Tinte
}) {
  return (
    <header
      className={cn(
        // A sangre sin márgenes negativos: el encabezado es hijo directo de
        // `<main>`, que no tiene relleno, así que ya llega de borde a borde. Con
        // `-mx` se salía 16 px por cada lado: el título quedaba pegado al borde
        // izquierdo, la acción cortada a la derecha y toda la pantalla con
        // desplazamiento horizontal. El relleno seguro devuelve el texto a su
        // sitio, también con el teléfono girado.
        'px-safe sticky top-0 z-30 backdrop-blur-xl',
        FONDO[tinte],
      )}
    >
      <div className="mx-auto flex max-w-lg items-center gap-3 pb-3 pt-safe">
        <div className="min-w-0 flex-1 pt-2">
          <h1 className="truncate font-display text-[22px] font-extrabold tracking-[-0.03em] text-ink">
            {title}
          </h1>
          {subtitle ? <p className="truncate text-[13px] text-ink-soft">{subtitle}</p> : null}
        </div>

        {action}
      </div>
    </header>
  )
}
